import { useState, useEffect } from 'react';
import { getAllGamesFollowList } from '@intelligence/services/following';
import useGetState from '@intelligence/hooks/useGetState';
import { PAGE_SIZES_SMALL } from '@intelligence/utils/tableUtils';

import type { PagingQuery } from 'tea-component';
import type { IPagingQuery, IFollowingGroup, IFollowingGame } from '@intelligence/pages/Following/Manage/types';

// 获取指定分组下游戏集合（公司集合）接口的入参
interface IFetchParams {
  // 用户id
  user_id: string;
  // 实体类型mobile、pc、console、company
  game_type: string;
  // 排序字段
  order_filed: string;
  // 排序方式
  order: 'desc' | 'asc' | string;
  // 游戏id（公司id）
  search_id: string;
  // 分组key
  group_name: string;
  // 页码
  page: number;
  // 页大小
  page_size: number;
}

type IPropsFetchParams = Omit<IFetchParams, 'group_name' | 'order_filed' | 'order' | 'page' | 'page_size'>;

type IInnerFetchParams = Pick<IFetchParams, 'group_name' | 'order_filed' | 'order' | 'page' | 'page_size'>;

const DEFAULT_PAGE_SIZE = PAGE_SIZES_SMALL[0];

const DEFAULT_PAGING_DATA = { pageIndex: 1, pageSize: DEFAULT_PAGE_SIZE };

export default function useFollowingGamesPagable(props: {
  // 分组下的游戏是否开启懒加载，即点击展开分组的时候 才加载对应分组下的游戏集合
  lazy?: boolean;
  // 游戏集合（公司集合）的取值字段
  gamesField: 'GameData' | 'company_data' | (string & {});
  // 默认排序字段
  initialOrderField?: string;
  // 默认排序方式
  initialOrder?: string;
  // 当前分组集合
  groups: IFollowingGroup[];
  // 当前展开的分组key集合
  expandedGroups?: string[];
  // 获取指定分组下游戏集合（公司集合）接口的入参
  fetchParams: IPropsFetchParams;
}) {
  const {
    lazy = true,
    gamesField,
    initialOrderField = 'following_time',
    initialOrder = 'desc',
    groups,
    expandedGroups,
    fetchParams,
  } = props;

  // 分组key与分组初始化状态的map映射（键为分组key、值为分组是否初始化，即分组是否已首次展开并且加载游戏集合（公司集合），首次展开分组后，会保留分组的游戏集合（公司集合）、分页状态，后面再展开时就不从服务端查询数据了）
  const [initeds, setIniteds] = useState<Record<string, boolean>>({});
  // 分组key与分组排序状态的map映射（键为分组key、值为分组对应的排序状态）
  const [sorts, setSorts] = useState<Record<string, any[]>>({});
  // 分组key与分组分页状态的map映射（键为分组key、值为分组对应的分页状态）
  const [pagingQuerys, setPagingQuerys] = useState<Record<string, IPagingQuery>>({});
  // 分组key与分组loading状态的map映射（键为分组key、值为分组对应的loading状态）
  const [loadings, setLoadings, getLoadings] = useGetState<Record<string, boolean>>({});
  // 分组key与分组游戏集合（公司集合）的map映射（键为分组key、值为分组对应的游戏集合（公司集合））
  const [games, setGames, getGames] = useGetState<Record<string, IFollowingGame[]>>({});

  /**
   * 获取指定分组下的游戏集合
   * @param params 参数对象
   * @param params.group_name 分组key
   * @param params.page 页码
   * @param params.page_size 页大小
   * @returns 游戏集合
   */
  const getFollowingGamesByGroup = async (params: IInnerFetchParams): Promise<IFollowingGame[]> => {
    let groupGames: IFollowingGame[] = [];
    try {
      const res = await getAllGamesFollowList({
        ...fetchParams,
        ...params,
        order_filed: params?.order_filed || initialOrderField,
        order: params?.order || initialOrder,
        page: params?.page || 1,
        page_size: params?.page_size || DEFAULT_PAGE_SIZE,
        unified_ids: [],
        unified_ids_old: null,
        unified_ids_new: null,
      });
      groupGames = res?.data?.[gamesField] || [];
    } catch (exception) {
      groupGames = [];
    }
    return Array.isArray(groupGames) ? groupGames : [];
  };

  /**
   * 更新指定分组的loading状态
   * @param group 分组
   * @param loading loading状态
   */
  const setLoadingsByGroup = (group: string, loading: boolean) => {
    if (!group) return;
    const loadings = getLoadings();
    setLoadings({
      ...loadings,
      [group]: loading,
    });
  };

  /**
   * 更新指定分组下的游戏集合（公司集合）
   * @param params 参数对象
   * @param params.group_name 分组key
   * @param params.order_filed 排序字段
   * @param params.order 排序方式
   * @param params.page 页码
   * @param params.page_size 页大小（即一页多少条数据）
   */
  const updateFollowingGamesByGroup = async (params: IInnerFetchParams) => {
    const groupName = params?.group_name || '';
    setLoadingsByGroup(groupName, true);
    const groupGames = await getFollowingGamesByGroup(params);
    const games = getGames();
    setGames({
      ...games,
      [groupName]: groupGames,
    });
    setLoadingsByGroup(groupName, false);
  };

  const handleGroupSortChange = (group: string, sort: any[]) => {
    if (!group || !sort) return;

    const firstSort = sort?.[0];
    const pagingQuery = pagingQuerys?.[group];

    setSorts({
      ...sorts,
      [group]: sort,
    });

    setPagingQuerys({
      ...pagingQuerys,
      [group]: {
        ...pagingQuery,
        pageIndex: 1,
      },
    });

    updateFollowingGamesByGroup({
      group_name: group,
      order_filed: firstSort?.by,
      order: firstSort?.order,
      page: 1,
      page_size: pagingQuery?.pageSize as number,
    });
  };

  /**
   * 指定分组分页change的回调
   * @param group 分组
   * @param pagingQuery 分页参数对象
   */
  const handleGroupPagingQueryChange = (group: string, pagingQuery: PagingQuery) => {
    if (!group || !pagingQuery) return;

    const groupSort = sorts?.[group]?.[0];

    setPagingQuerys({
      ...pagingQuerys,
      [group]: {
        ...pagingQuerys?.[group],
        ...pagingQuery,
      },
    });

    updateFollowingGamesByGroup({
      group_name: group,
      order_filed: groupSort?.by,
      order: groupSort?.order,
      page: pagingQuery?.pageIndex as number,
      page_size: pagingQuery?.pageSize as number,
    });
  };

  /**
   * 展开分组时的回调
   * @param group 分组
   */
  const handleGroupExpand = (group: string) => {
    if (!group) return;
    if (initeds?.[group]) return;
    const groupItem = groups?.find((item) => item?.entity_name === group);
    setIniteds({ ...initeds, [group]: true });
    if (!groupItem?.game_list_total) {
      setLoadingsByGroup(group, false);
      return;
    }
    const groupSort = sorts?.[group]?.[0];
    updateFollowingGamesByGroup({
      group_name: group,
      order_filed: groupSort?.by,
      order: groupSort?.order,
      page: DEFAULT_PAGING_DATA.pageIndex,
      page_size: DEFAULT_PAGING_DATA.pageSize,
    });
  };

  /**
   * 初始化
   * 根据分组，初始化initeds、 pagingQuerys、loadings、games
   */
  useEffect(() => {
    if (!Array.isArray(groups) || !groups.length) {
      setIniteds({});
      setPagingQuerys({});
      setLoadings({});
      setGames({});
      return;
    }

    const initedsTmp: Record<string, boolean> = {};
    const sortsTmp: Record<string, any[]> = {};
    const pagingQuerysTmp: Record<string, IPagingQuery> = {};
    const loadingsTmp: Record<string, boolean> = {};
    const gamesTmp: Record<string, IFollowingGame[]> = {};

    for (const group of groups) {
      const { entity_name: entityName, game_list_total: total } = group || {};
      if (!entityName) continue;
      if (!lazy || expandedGroups?.includes(entityName)) {
        initedsTmp[entityName] = true;
        updateFollowingGamesByGroup({
          group_name: entityName,
          order_filed: initialOrderField,
          order: initialOrder,
          page: DEFAULT_PAGING_DATA.pageIndex,
          page_size: DEFAULT_PAGING_DATA.pageSize,
        });
      } else {
        initedsTmp[entityName] = false;
      }
      sortsTmp[entityName] = [{ by: initialOrderField, order: initialOrder }];
      pagingQuerysTmp[entityName] = {
        ...DEFAULT_PAGING_DATA,
        total: total || 0,
      };
      loadingsTmp[entityName] = true;
      gamesTmp[entityName] = [];
    }

    setIniteds(initedsTmp);
    setSorts(sortsTmp);
    setPagingQuerys(pagingQuerysTmp);
    setLoadings(loadingsTmp);
    setGames(gamesTmp);
  }, [groups]);

  return {
    pagingQuerys,
    sorts,
    loadings,
    games,
    onGroupSortChange: handleGroupSortChange,
    onGroupPagingQueryChange: handleGroupPagingQueryChange,
    onGroupExpand: handleGroupExpand,
  };
}
